/**
 * @format
 * @module bundles/section
 * @description The bundle for section pages
 */
import timeSince from "ui/time-since";
import initFinancialSearch from "ui/financial-search";
import { setTarget as setPrimarySkipLinkTarget } from "ui/skip-link-primary";
import setupGlobeRecommendations from "lib/recommended/tgam";
import setupFinancialAutorefresh from "lib/financial/autorefresh-control";
import initMarketTickers from "lib/financial/markets";
import initStockReportsPlus from "lib/financial/stock-reports-plus";
import initStockCalcReport from "lib/financial/stockcalc-report";
import initValuEngineReport from "lib/financial/valuengine-report";
import initPressReleases from "lib/financial/press-releases";
import backtoTopButton from "lib/global/back-to-top-button";
import variantTracker from "lib/global/variant-tracker";
import tgamConfig from "config/tgam";
import { initPencilBanner } from "ui/dsw-pencil";
import initStoryFeed from "ui/story-feed";
import { getProp, qs, qsa } from "utils/utils";
import crow from "utils/crow";
import keytarDone from "lib/commercial/keytar/keytar-done";
import enableDismissLiveVideo from "lib/video/live-video";
import importAndDisplayContainers from "lib/marketing/import-and-display-containers";
import initEditorialBoardMessage from "ui/editorial-board-message";
import takeABreakInit from "ui/take-a-break";
import marketingContainersReady from "lib/marketing/marketing-containers-ready";
import datalayerReady from "lib/audience/datalayer/ready";
import adSequencingReady from "lib/commercial/ad-sequencing/ready";
import podcasts from "lib/podcasts";

function init() {
  // Market ticker component
  const siteName = getProp(tgamConfig, "meta.siteName", "");
  const financialPage = [
    "homepage",
    "investing",
    "business",
    "investing/globe-advisor/advisor-stocks"
  ].includes(siteName);
  if (financialPage) {
    initMarketTickers();
  }

  if (tgamConfig.meta.urlRelative === "/canada") {
    import("lib/sophi-automator/canada" /* webpackChunkName: "sophi-canada-regionalization" */)
      .then(canada => {
        canada.default();
      })
      .catch(console.error);
  }

  if (tgamConfig.meta.urlRelative === "/business") {
    const isAdmin = getProp(tgamConfig, "env.isPbAdmin", false);
    if (isAdmin) {
      return;
    }
    const { bizSampleSize = 0, bizKey = "", bizTestEnabled = false } = getProp(
      tgamConfig,
      "sophiProps",
      {}
    );
    if (!bizTestEnabled) {
      console.info("[Sophi Automator Business test] Abort: missing key or sample size");
      return;
    }
    import("lib/sophi-automator/business" /* webpackChunkName: "sophi-business-automator-test" */)
      .then(biz => {
        biz.default({
          name: bizKey,
          percentage: bizSampleSize
        });
      })
      .catch(e => {
        console.error("[Sophi Automator Business test] caught error:", e);
      });
  }

  datalayerReady.then(() => {
    if (tgamConfig.meta.urlRelative === "/investing/globe-advisor") {
      import("ui/globe-advisor-reader-tip" /* webpackChunkName: "globe-advisor-tool-tip" */)
        .then(tooltip => {
          tooltip.default();
        })
        .catch(console.error);
    }
  });

  // Datalayer should be ready in order to determine what newsletters a user is subscribed to
  Promise.all([adSequencingReady, datalayerReady]).then(() => {
    setupGlobeRecommendations();
    if (tgamConfig.meta.urlRelative.includes("/union")) {
      import("lib/union/render" /* webpackChunkName: "union-stories" */)
        .then(m => m.default())
        .catch(console.error);
    }
  });

  keytarDone.then(data => {
    // Similar placement to when the "Arc Ready" event is fired.
    // We can remove this method once CDN section tests are completed.
    variantTracker();

    if (data.accessAllowed) {
      initStockReportsPlus();
      initStockCalcReport();
      initValuEngineReport();
      initPressReleases();
      initPencilBanner();
    }
  });

  // Marketing containers powered by recommender
  marketingContainersReady.then(meta => {
    const mcSections = getProp(tgamConfig, "env.marketContainerRecommenderSections", "").split(",");
    const urlRelative = getProp(tgamConfig, "meta.urlRelative");
    if (mcSections.includes(urlRelative)) {
      importAndDisplayContainers(meta);
    }
  });

  if (tgamConfig.meta.isHomePbLayout) {
    const focusableElements = qsa("a, button, [href], input, select, textarea", qs(".l-container"));
    // Get the first element that isn't a child of a hidden container
    const firstFocusableElement = focusableElements.find(e => !e.closest(".u-hidden"));
    if (firstFocusableElement) {
      setPrimarySkipLinkTarget(firstFocusableElement);
    }
  }

  // Start Election 2021
  // if (tgamConfig.meta.isHomePage) {
  //   const electionEnabled = getProp(tgamConfig, "env.electionEnabled", "false");
  //   if (electionEnabled === "true") {
  //     import("lib/election/homepage" /* webpackChunkName: "election-homepage" */)
  //       .then(elections => {
  //         elections.default();
  //       })
  //       .catch(console.error);
  //   }
  // }
  // End Election 2021

  if (
    tgamConfig.meta.isHomePbLayout ||
    tgamConfig.meta.isPremiumPbLayout ||
    tgamConfig.meta.isTemplatePbLayout
  ) {
    import("ui/follow-topics" /* webpackChunkName: "follow-topics" */)
      .then(followTopics => {
        followTopics.default();
      })
      .catch(console.error);

    if (tgamConfig.meta.isTopicTemplate) {
      import("lib/topics" /* webpackChunkName: "load-more-topics" */)
        .then(loadMore => {
          loadMore.init();
        })
        .catch(console.error);
    }
  }

  timeSince();
  enableDismissLiveVideo();
  setupFinancialAutorefresh();
  initFinancialSearch();
  initStoryFeed();
  initEditorialBoardMessage();
  backtoTopButton();
  takeABreakInit();
  podcasts();

  if (/\/investing\/markets/.test(window.location.pathname)) {
    import("lib/financial/recently-viewed" /* webpackChunkName: "recent-tickers" */)
      .then(m => m.default())
      .catch(console.error);
  }
}

crow.context(
  {
    feature: "bundle : section"
  },
  init
);
